import './App.css';
import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaMapMarkerAlt, FaPhone, FaEnvelope, FaLock, FaUserShield } from 'react-icons/fa';
import Home from './pages/Home';
import Testimonies from './pages/Testimonies';
import OurMission from './pages/OurMission';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginModal from './auth/LoginModal';
import { EVENT } from './data/eventInfo';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Navbar({ onOpenLogin }) {
  const [navVisible, setNavVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    if (pathname !== '/') {
      setNavVisible(true);
      return;
    }
    setNavVisible(false);
    const handleScroll = () => {
      if (window.scrollY > 100) setNavVisible(true);
      else setNavVisible(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pathname]);

  return (
    <nav className={`navbar ${navVisible ? 'navbar-visible' : ''}`}>
      <div className="nav-container">
        <Link to="/" className="logo" onClick={() => setMenuOpen(false)}>
          <img src="/logo.png" alt="WCMC" className="logo-icon" />
          <span>West Coast Marriage Camp</span>
        </Link>
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>
        <ul className={`nav-menu ${menuOpen ? 'nav-open' : ''}`}>
          <li><NavLink to="/" end onClick={() => setMenuOpen(false)}>Home</NavLink></li>
          <li><NavLink to="/our-mission" onClick={() => setMenuOpen(false)}>Our Mission</NavLink></li>
          <li><NavLink to="/testimonies" onClick={() => setMenuOpen(false)}>Testimonies</NavLink></li>
          <li>
            <a href={EVENT.donateUrl} target="_blank" rel="noopener noreferrer" onClick={() => setMenuOpen(false)}>
              Donate
            </a>
          </li>
          <li>
            <a href={EVENT.registrationOpen ? EVENT.registerUrl : '#dates'} target={EVENT.registrationOpen ? '_blank' : undefined} rel="noopener noreferrer" className="nav-register" onClick={() => setMenuOpen(false)}>
              {EVENT.registrationOpen ? 'Register' : 'Register Soon'}
            </a>
          </li>
          <li>
            <button
              type="button"
              className={`nav-lock ${user ? 'nav-lock-signed-in' : ''}`}
              onClick={() => { setMenuOpen(false); onOpenLogin(); }}
              title={user ? (isAdmin ? 'Signed in (Admin)' : 'Signed in') : 'Admin sign in'}
              aria-label="Admin sign in"
            >
              {user ? <FaUserShield /> : <FaLock />}
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-contact">
          <div className="contact-item">
            <FaMapMarkerAlt className="contact-icon" />
            <span>{EVENT.hqLocation}</span>
          </div>
          <div className="contact-item">
            <FaPhone className="contact-icon" />
            <a href={`tel:${EVENT.phoneTel}`}>{EVENT.phone}</a>
          </div>
          <div className="contact-item">
            <FaEnvelope className="contact-icon" />
            <a href={`mailto:${EVENT.email}`}>{EVENT.email}</a>
          </div>
        </div>
        <div className="footer-brand">
          <p className="footer-tagline">Walking with God together.</p>
          <p className="footer-presents">West Coast Marriage Camp</p>
          <p className="footer-fineprint">
            West Coast Marriage Camp is supported by West Coast Ministries, a 501(c)(3) California <span className="nowrap">non-profit</span>.
          </p>
          <p className="footer-fineprint">
            Honored to utilize <em>The Rendezvous Project</em> from Zoweh Ministries.
          </p>
          <p className="footer-copyright">&copy; {new Date().getFullYear()} West Coast Marriage Camp</p>
          <p className="footer-fineprint" style={{ marginTop: 8, opacity: 0.5 }}>
            Built by <a href="https://appcatalyst.org" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>App Catalyst LLC</a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function AppShell() {
  const [loginOpen, setLoginOpen] = useState(false);
  return (
    <>
      <ScrollToTop />
      <div className="App">
        <Navbar onOpenLogin={() => setLoginOpen(true)} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/our-mission" element={<OurMission />} />
          {/* old link, kept so anything already shared still lands in the right place */}
          <Route path="/our-story" element={<Navigate to="/our-mission" replace />} />
          <Route path="/testimonies" element={<Testimonies />} />
        </Routes>
        <Footer />
        {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}
      </div>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
